/**
 * Chemistry limits by grade (P5, decision D4 of docs/plans/2026-10-27_cwc-meeting-follow-ups.md): one editor over the
 * two homes the platform already reads. A grade's minimum and maximum are its process windows (the Limits page's; the
 * roll-ups judge the grade's heats against them), and its target is the org's own row of the grade aim library, over
 * the shipped starter library's. A target is tier AIM: a methoding target, never a measured value. The Go mirror is
 * platform-core/internal/twin/grade_chemistry.go; the shapes are api/openapi.yaml's GradeChemistry, mirrored by hand.
 *
 *   GET /v1/twin/grade-chemistry               any member: every grade with its sources and its elements
 *   PUT /v1/twin/grade-chemistry/{grade}       model.write: {elements: [{element, target?, min?, max?, unit}]}, in one
 *                                              transaction; an element left out is untouched, one with no end closes
 *                                              its window, one with no target removes the plant's own target
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api, ApiClientError } from "@/lib/api-client";
import { BRIEF_LIST_KEY, CADENCE_BRIEF_KEY, useBriefLive } from "@/lib/api/brief";
import {
  GRADE_CHEMISTRY_KEY,
  limitsSaveErrorText,
  PROCESS_WINDOW_PARAMETERS_KEY,
  PROCESS_WINDOWS_KEY,
} from "@/lib/api/limits";

export { GRADE_CHEMISTRY_KEY };

/** Where a grade is listed from: the plant's heats, its products, its windows, its targets, or the shipped catalog. */
export type GradeSource = "heats" | "products" | "windows" | "targets" | "catalog";

/** Where a value comes from: the plant's own, the plant's window for every grade, or the starter library. */
export type GradeValueSource = "plant" | "plant_every_grade" | "library";

/** One element of a grade: its target, minimum and maximum, each with where it comes from. */
export interface GradeChemistryElement {
  /** The name a window is set on (the analysis's name; the library's Mg_resid is residual_mg). */
  element: string;
  label: string;
  unit: string;
  /** Tier AIM: a target, never a measurement. */
  target?: number;
  target_source?: "plant" | "library";
  min?: number;
  max?: number;
  limits_source?: GradeValueSource;
  set_by_name?: string;
  set_at?: string;
  note?: string;
  /** A window can be set on it: a parameter the plant records or the playbook names. */
  settable: boolean;
}

export interface GradeChemistryGrade {
  grade: string;
  name?: string;
  route?: string;
  standard?: string;
  sources: GradeSource[];
  heats: number;
  products: number;
  elements: GradeChemistryElement[];
}

/** A chemistry name a window can be set on, to add an element. */
export interface GradeChemistryParameter {
  element: string;
  label: string;
  unit: string;
}

export interface GradeChemistry {
  grades: GradeChemistryGrade[];
  parameters: GradeChemistryParameter[];
  /** Every target's tier: AIM. */
  target_tier: string;
  heat_days: number;
}

/** One element of a write: absent ends close its window, an absent target removes the plant's own. */
export interface GradeChemistryElementWrite {
  element: string;
  target?: number;
  min?: number;
  max?: number;
  unit: string;
}

export interface GradeChemistryWrite {
  elements: GradeChemistryElementWrite[];
}

const list = <T>(v: T[] | null | undefined): T[] => v ?? [];

/** The document as the page reads it: a Go nil list as empty. */
export function gradeChemistryOf(raw: Partial<GradeChemistry> | null | undefined): GradeChemistry {
  return {
    grades: list(raw?.grades).map((g) => ({ ...g, sources: list(g.sources), elements: list(g.elements) })),
    parameters: list(raw?.parameters),
    target_tier: raw?.target_tier ?? "AIM",
    heat_days: raw?.heat_days ?? 730,
  };
}

/**
 * A grade of the document by the name a product carries: the same text first, else the same letters in another case
 * (a product typed "gr-500s" for the plant's GR-500S); undefined for none.
 */
export function gradeChemistryFor(doc: GradeChemistry | null | undefined, grade: string): GradeChemistryGrade | undefined {
  const g = grade.trim();
  if (!doc || !g) return undefined;
  return doc.grades.find((x) => x.grade === g) ?? doc.grades.find((x) => x.grade.toLowerCase() === g.toLowerCase());
}

/** The document; null where the platform does not serve the route yet (404), so the section says so. */
export async function fetchGradeChemistry(): Promise<GradeChemistry | null> {
  try {
    return gradeChemistryOf(await api.get<GradeChemistry>("/v1/twin/grade-chemistry"));
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 404) return null;
    throw err;
  }
}

export async function saveGradeChemistry(grade: string, body: GradeChemistryWrite): Promise<GradeChemistry> {
  return gradeChemistryOf(await api.put<GradeChemistry>(`/v1/twin/grade-chemistry/${encodeURIComponent(grade)}`, body));
}

/**
 * The document, read again after five minutes: every save that moves it (this section's, the Limits page's, a
 * consultant's card) marks it stale at once (limits.ts windowsSaved), so a long stale time costs no freshness and spares
 * the read on every product sheet open.
 */
export function useGradeChemistryQuery(enabled = true) {
  const live = useBriefLive();
  return useQuery({
    queryKey: GRADE_CHEMISTRY_KEY,
    queryFn: fetchGradeChemistry,
    enabled: live && enabled,
    staleTime: 5 * 60_000,
  });
}

/**
 * What a grade's save does to the cache: keeps the answer, and marks stale what its windows change, the Limits page's
 * windows and parameters, and the briefs (read again once the refresh the save queued has run).
 */
export function gradeChemistrySaved(qc: Pick<QueryClient, "setQueryData" | "invalidateQueries">, doc: GradeChemistry): void {
  qc.setQueryData(GRADE_CHEMISTRY_KEY, doc);
  void qc.invalidateQueries({ queryKey: PROCESS_WINDOWS_KEY });
  void qc.invalidateQueries({ queryKey: PROCESS_WINDOW_PARAMETERS_KEY });
  void qc.invalidateQueries({ queryKey: CADENCE_BRIEF_KEY });
  void qc.invalidateQueries({ queryKey: BRIEF_LIST_KEY });
}

export function useSaveGradeChemistry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ grade, body }: { grade: string; body: GradeChemistryWrite }) => saveGradeChemistry(grade, body),
    onSuccess: (doc) => gradeChemistrySaved(qc, doc),
  });
}

/** What the section says when a save fails: the platform's own words where it refused a value, else the Limits page's. */
export function gradeChemistrySaveErrorText(err: unknown): string {
  if (err instanceof ApiClientError) {
    const body = err.body as { error?: unknown; detail?: unknown } | null;
    const detail = typeof body?.detail === "string" ? body.detail : "";
    if ((body?.error === "invalid_element" || body?.error === "invalid_grade") && detail) {
      return `The platform did not save it: ${detail}.`;
    }
  }
  return limitsSaveErrorText(err);
}
